import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, RefreshCw, Search, ChevronDown, ChevronUp,
  Plus, Minus, Bell, CreditCard, Edit3,
  CheckCircle, XCircle, AlertTriangle, Shield,
  ArrowUpCircle, ArrowDownCircle, Send, Lock, Unlock,
  PlusCircle, LogOut, BarChart3, ArrowLeftRight,
  DollarSign, UserCheck,
} from 'lucide-react';

const SUPABASE_URL = 'https://jqvqkknqdvhsvlxlhwka.supabase.co';
const ADMIN_TOKEN = 'CYF24_SUPER_ADMIN_TOKEN_2026';
const API = `${SUPABASE_URL}/functions/v1/cyf24-admin-api-2026`;

interface BankAccount {
  id: string;
  user_id: string;
  account_number: string;
  sort_code: string;
  iban: string;
  bic: string;
  balance: number;
  available_balance: number;
  currency: string;
  status: string;
}

interface Client {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  kyc_status?: string;
  plan?: string;
  is_admin?: boolean;
  created_at?: string;
  bank_accounts?: BankAccount[];
}

interface Transaction {
  id: string;
  type: string;
  amount: number;
  description: string;
  created_at: string;
  status: string;
  reference?: string;
  balance_after?: number;
}

type Tab = 'account' | 'credit' | 'debit' | 'transaction' | 'notify' | 'profile' | 'freeze' | 'loan' | 'verify' | 'addaccount';

async function adminApi(method: string, action: string, body?: object) {
  const url = `${API}?action=${action}`;
  const res = await fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Admin-Token': ADMIN_TOKEN,
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return res.json();
}

function fmt(amount: number) {
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);
}

function fmtDate(d?: string) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB');
}

const KYC: Record<string, { label: string; cls: string }> = {
  verified: { label: '✓ Zweryfikowany', cls: 'bg-green-100 text-green-700 border-green-200' },
  submitted: { label: '⏳ W trakcie', cls: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  pending: { label: '⚠ Oczekuje', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  rejected: { label: '✗ Odrzucony', cls: 'bg-red-100 text-red-700 border-red-200' },
};

function Toast({ msg, ok, onClose }: { msg: string; ok: boolean; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 4000);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-xl shadow-2xl border flex items-center gap-3 text-sm font-medium animate-fade-in ${ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
      {ok ? <CheckCircle className="w-5 h-5 text-green-600 shrink-0" /> : <XCircle className="w-5 h-5 text-red-500 shrink-0" />}
      {msg}
      <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none">×</button>
    </div>
  );
}

function ClientRow({ client, onRefresh, notify }: {
  client: Client;
  onRefresh: () => void;
  notify: (msg: string, ok: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('account');
  const [loading, setLoading] = useState(false);
  const [txList, setTxList] = useState<Transaction[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);

  const [creditAmt, setCreditAmt] = useState('');
  const [creditNote, setCreditNote] = useState('');
  const [debitAmt, setDebitAmt] = useState('');
  const [debitNote, setDebitNote] = useState('');
  const [txType, setTxType] = useState('credit');
  const [txAmt, setTxAmt] = useState('');
  const [txDesc, setTxDesc] = useState('');
  const [txCat, setTxCat] = useState('other');
  const [notifTitle, setNotifTitle] = useState('');
  const [notifMsg, setNotifMsg] = useState('');
  const [notifType, setNotifType] = useState('info');
  const [editAccNum, setEditAccNum] = useState('');
  const [editSortCode, setEditSortCode] = useState('');
  const [editIban, setEditIban] = useState('');
  const [editBic, setEditBic] = useState('');
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editKyc, setEditKyc] = useState('');
  const [editPlan, setEditPlan] = useState('');
  const [loanAmt, setLoanAmt] = useState('');
  const [loanType, setLoanType] = useState('personal');
  const [loanTerm, setLoanTerm] = useState('12');

  const acc = client.bank_accounts?.[0];

  const loadTx = async () => {
    if (!acc) return;
    setLoadingTx(true);
    const data = await adminApi('GET', `transactions&account_id=${acc.id}`);
    setTxList(data.transactions ?? []);
    setLoadingTx(false);
  };

  const handleOpen = () => {
    if (!open) {
      setEditAccNum(acc?.account_number ?? '');
      setEditSortCode(acc?.sort_code ?? '');
      setEditIban(acc?.iban ?? '');
      setEditBic(acc?.bic ?? '');
      setEditFirstName(client.first_name ?? '');
      setEditLastName(client.last_name ?? '');
      setEditPhone(client.phone ?? '');
      setEditKyc(client.kyc_status ?? 'pending');
      setEditPlan(client.plan ?? 'Standard');
      loadTx();
    }
    setOpen(v => !v);
  };

  const run = async (fn: () => Promise<unknown>) => {
    setLoading(true);
    try {
      const r: { success?: boolean; error?: string } = await fn() as { success?: boolean; error?: string };
      if (r?.error) {
        notify('Błąd: ' + r.error, false);
      } else {
        notify('Operacja zakończona pomyślnie ✓', true);
        onRefresh();
        if (acc) loadTx();
      }
    } catch (e) {
      notify(String(e), false);
    }
    setLoading(false);
  };

  const kycInfo = KYC[client.kyc_status ?? 'pending'] ?? KYC.pending;

  const interestRate = loanType === 'personal' ? 5.9 : loanType === 'business' ? 4.5 : 3.2;
  const monthlyPayment = loanAmt ? (parseFloat(loanAmt) * (1 + interestRate / 100)) / parseInt(loanTerm) : 0;

  const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'account', label: 'Konto', icon: <CreditCard className="w-3.5 h-3.5" /> },
    { id: 'credit', label: 'Kredyt +', icon: <ArrowUpCircle className="w-3.5 h-3.5" /> },
    { id: 'debit', label: 'Debet −', icon: <ArrowDownCircle className="w-3.5 h-3.5" /> },
    { id: 'transaction', label: 'Dodaj Tx', icon: <ArrowLeftRight className="w-3.5 h-3.5" /> },
    { id: 'notify', label: 'Powiadomienie', icon: <Bell className="w-3.5 h-3.5" /> },
    { id: 'profile', label: 'Profil', icon: <Edit3 className="w-3.5 h-3.5" /> },
    { id: 'freeze', label: 'Status', icon: <Shield className="w-3.5 h-3.5" /> },
    { id: 'loan', label: 'Pożyczka', icon: <DollarSign className="w-3.5 h-3.5" /> },
    { id: 'verify', label: 'Weryfikuj KYC', icon: <UserCheck className="w-3.5 h-3.5" /> },
    { id: 'addaccount', label: 'Dodaj Konto', icon: <PlusCircle className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="border border-gray-200 rounded-2xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50" onClick={handleOpen}>
        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm shrink-0">
          {(client.first_name?.[0] ?? '?').toUpperCase()}{(client.last_name?.[0] ?? '').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900">{client.first_name} {client.last_name}</p>
          <p className="text-xs text-gray-500 truncate">{client.email}</p>
        </div>
        <div className="hidden sm:flex items-center gap-2 shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${kycInfo.cls}`}>{kycInfo.label}</span>
          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">{client.plan ?? 'Standard'}</span>
          <span className={`text-sm font-bold ${acc?.status === 'frozen' ? 'text-red-600' : 'text-gray-900'}`}>
            {acc ? (acc.status === 'frozen' ? '🔒 Zamrożone' : fmt(acc.balance ?? 0)) : '—'}
          </span>
        </div>
        <span className="text-xs text-gray-400 hidden md:block">{fmtDate(client.created_at)}</span>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </div>

      {open && (
        <div className="border-t border-gray-100">
          <div className="flex gap-1 p-3 bg-gray-50 border-b border-gray-100 overflow-x-auto">
            {TABS.map(t => (
              <button key={t.id} onClick={() => { setTab(t.id); if (t.id === 'account') loadTx(); }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${tab === t.id ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'text-gray-500 hover:text-gray-900 hover:bg-gray-100'}`}>
                {t.icon}{t.label}
              </button>
            ))}
          </div>

          <div className="p-4">

            {tab === 'account' && (
              <div className="space-y-4">
                {acc ? (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Saldo', val: fmt(acc.balance ?? 0), color: 'text-green-700' },
                        { label: 'Nr konta', val: acc.account_number || '—', color: 'text-gray-900' },
                        { label: 'Sort Code', val: acc.sort_code || '—', color: 'text-gray-900' },
                        { label: 'Status', val: acc.status === 'active' ? '● Aktywne' : '⛔ Zamrożone', color: acc.status === 'active' ? 'text-green-700' : 'text-red-600' },
                      ].map(item => (
                        <div key={item.label} className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                          <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                          <p className={`font-mono text-sm font-semibold ${item.color}`}>{item.val}</p>
                        </div>
                      ))}
                    </div>
                    <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">IBAN / BIC</p>
                      <p className="font-mono text-xs text-gray-800">{acc.iban || '—'} · {acc.bic || '—'}</p>
                    </div>

                    <details className="border border-gray-200 rounded-xl overflow-hidden">
                      <summary className="p-3 bg-gray-50 cursor-pointer text-sm font-semibold text-gray-700 hover:bg-gray-100">
                        ✏️ Edytuj szczegóły konta
                      </summary>
                      <div className="p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-gray-500">Numer konta</label>
                            <input className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-white" value={editAccNum} onChange={e => setEditAccNum(e.target.value)} />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Sort Code</label>
                            <input className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-white" value={editSortCode} onChange={e => setEditSortCode(e.target.value)} />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">IBAN</label>
                          <input className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-white" value={editIban} onChange={e => setEditIban(e.target.value)} />
                        </div>
                        <div>
                          <label className="text-xs text-gray-500">BIC / SWIFT</label>
                          <input className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono bg-white" value={editBic} onChange={e => setEditBic(e.target.value)} />
                        </div>
                        <button disabled={loading} onClick={() => run(() => adminApi('POST', 'update_account', { type: 'update_account', account_id: acc.id, account_number: editAccNum, sort_code: editSortCode, iban: editIban, bic: editBic }))}
                          className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50 transition-colors">
                          {loading ? 'Zapisywanie...' : 'Zapisz szczegóły konta'}
                        </button>
                      </div>
                    </details>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-semibold text-gray-900">Historia transakcji</p>
                        <button onClick={loadTx} className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-900">
                          <RefreshCw className={`w-3 h-3 ${loadingTx ? 'animate-spin' : ''}`} /> Odśwież
                        </button>
                      </div>
                      {loadingTx ? (
                        <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-9 bg-gray-100 rounded-lg animate-pulse" />)}</div>
                      ) : txList.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">Brak transakcji</p>
                      ) : (
                        <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
                          {txList.map(tx => {
                            const pos = ['credit', 'transfer_in', 'deposit'].includes(tx.type);
                            return (
                              <div key={tx.id} className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 text-xs">
                                <div className="flex items-center gap-2 min-w-0">
                                  <span className={`w-2 h-2 rounded-full shrink-0 ${pos ? 'bg-green-500' : 'bg-red-400'}`} />
                                  <span className="text-gray-700 truncate max-w-48">{tx.description}</span>
                                </div>
                                <div className="flex items-center gap-3 shrink-0">
                                  <span className={`font-semibold ${pos ? 'text-green-600' : 'text-red-500'}`}>{pos ? '+' : '-'}{fmt(tx.amount)}</span>
                                  <span className="text-gray-400">{fmtDate(tx.created_at)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500 mb-4">Brak konta bankowego dla tego klienta</p>
                    <button disabled={loading} onClick={() => run(() => adminApi('POST', 'create_account', { type: 'create_account', user_id: client.id }))}
                      className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold disabled:opacity-50">
                      {loading ? 'Tworzenie...' : 'Utwórz konto bankowe'}
                    </button>
                  </div>
                )}
              </div>
            )}

            {tab === 'credit' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm mb-1">
                  <ArrowUpCircle className="w-5 h-5" /> Nalicz środki klientowi
                </div>
                <p className="text-xs text-gray-500">Aktualne saldo: <strong>{acc ? fmt(acc.balance) : '—'}</strong></p>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Kwota (GBP) *</label>
                  <input type="number" min="0.01" step="0.01" placeholder="np. 100.00"
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={creditAmt} onChange={e => setCreditAmt(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Notatka (opcjonalnie)</label>
                  <input placeholder="Powód naliczenia..."
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={creditNote} onChange={e => setCreditNote(e.target.value)} />
                </div>
                <button disabled={loading || !creditAmt || !acc}
                  onClick={() => run(() => adminApi('POST', 'credit', { type: 'credit', account_id: acc!.id, user_id: client.id, amount: parseFloat(creditAmt), note: creditNote }))}
                  className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <Plus className="w-4 h-4" />{loading ? 'Przetwarzanie...' : `Nalicz £${parseFloat(creditAmt || '0').toFixed(2)}`}
                </button>
              </div>
            )}

            {tab === 'debit' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-red-600 font-semibold text-sm mb-1">
                  <ArrowDownCircle className="w-5 h-5" /> Obciąż konto klienta
                </div>
                <p className="text-xs text-gray-500">Aktualne saldo: <strong>{acc ? fmt(acc.balance) : '—'}</strong></p>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Kwota (GBP) *</label>
                  <input type="number" min="0.01" step="0.01" placeholder="np. 50.00"
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={debitAmt} onChange={e => setDebitAmt(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Notatka (opcjonalnie)</label>
                  <input placeholder="Powód obciążenia..."
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={debitNote} onChange={e => setDebitNote(e.target.value)} />
                </div>
                <button disabled={loading || !debitAmt || !acc}
                  onClick={() => run(() => adminApi('POST', 'debit', { type: 'debit', account_id: acc!.id, user_id: client.id, amount: parseFloat(debitAmt), note: debitNote }))}
                  className="w-full py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <Minus className="w-4 h-4" />{loading ? 'Przetwarzanie...' : `Obciąż £${parseFloat(debitAmt || '0').toFixed(2)}`}
                </button>
              </div>
            )}

            {tab === 'transaction' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm mb-1">
                  <ArrowLeftRight className="w-5 h-5" /> Dodaj niestandardową transakcję
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Typ transakcji *</label>
                  <select className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={txType} onChange={e => setTxType(e.target.value)}>
                    <option value="credit">Kredyt (przychodzące)</option>
                    <option value="debit">Debet (wychodzące)</option>
                    <option value="transfer_in">Przelew przychodzący</option>
                    <option value="transfer_out">Przelew wychodzący</option>
                    <option value="payment">Płatność</option>
                    <option value="salary">Wynagrodzenie</option>
                    <option value="deposit">Depozyt</option>
                    <option value="withdrawal">Wypłata</option>
                    <option value="fee">Opłata</option>
                    <option value="interest">Odsetki</option>
                    <option value="refund">Zwrot</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Kwota (GBP) *</label>
                  <input type="number" min="0.01" step="0.01" placeholder="np. 200.00"
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={txAmt} onChange={e => setTxAmt(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Opis *</label>
                  <input placeholder="np. Wypłata, Czynsz, itp."
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={txDesc} onChange={e => setTxDesc(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Kategoria</label>
                  <select className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={txCat} onChange={e => setTxCat(e.target.value)}>
                    <option value="other">Inne</option>
                    <option value="salary">Wynagrodzenie</option>
                    <option value="transfer">Przelew</option>
                    <option value="shopping">Zakupy</option>
                    <option value="food">Jedzenie</option>
                    <option value="transport">Transport</option>
                    <option value="utilities">Media</option>
                    <option value="entertainment">Rozrywka</option>
                    <option value="healthcare">Zdrowie</option>
                    <option value="rent">Czynsz</option>
                    <option value="tax">Podatek</option>
                    <option value="fee">Opłata</option>
                  </select>
                </div>
                <button disabled={loading || !txAmt || !txDesc || !acc}
                  onClick={() => run(() => adminApi('POST', 'add_transaction', { type: 'add_transaction', account_id: acc!.id, user_id: client.id, tx_type: txType, amount: parseFloat(txAmt), description: txDesc, category: txCat }))}
                  className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <PlusCircle className="w-4 h-4" />{loading ? 'Dodawanie...' : 'Dodaj transakcję'}
                </button>
              </div>
            )}

            {tab === 'notify' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-purple-700 font-semibold text-sm mb-1">
                  <Bell className="w-5 h-5" /> Wyślij powiadomienie do {client.first_name}
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Typ</label>
                  <select className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={notifType} onChange={e => setNotifType(e.target.value)}>
                    <option value="info">ℹ️ Informacja</option>
                    <option value="success">✅ Sukces</option>
                    <option value="warning">⚠️ Ostrzeżenie</option>
                    <option value="alert">🚨 Alert</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Tytuł *</label>
                  <input placeholder="Tytuł powiadomienia..."
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={notifTitle} onChange={e => setNotifTitle(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Wiadomość *</label>
                  <textarea rows={4} placeholder="Treść powiadomienia..."
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white resize-none"
                    value={notifMsg} onChange={e => setNotifMsg(e.target.value)} />
                </div>
                <button disabled={loading || !notifTitle || !notifMsg}
                  onClick={() => run(() => adminApi('POST', 'notify', { type: 'notify', user_id: client.id, title: notifTitle, message: notifMsg, notif_type: notifType }))}
                  className="w-full py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <Send className="w-4 h-4" />{loading ? 'Wysyłanie...' : 'Wyślij powiadomienie'}
                </button>
              </div>
            )}

            {tab === 'profile' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-gray-700 font-semibold text-sm mb-1">
                  <Edit3 className="w-5 h-5" /> Edytuj profil klienta
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Imię</label>
                    <input className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                      value={editFirstName} onChange={e => setEditFirstName(e.target.value)} />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Nazwisko</label>
                    <input className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                      value={editLastName} onChange={e => setEditLastName(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Telefon</label>
                  <input className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                    value={editPhone} onChange={e => setEditPhone(e.target.value)} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Status KYC</label>
                    <select className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                      value={editKyc} onChange={e => setEditKyc(e.target.value)}>
                      <option value="pending">Oczekuje</option>
                      <option value="submitted">Przesłane</option>
                      <option value="verified">Zweryfikowane</option>
                      <option value="rejected">Odrzucone</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Plan</label>
                    <select className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"
                      value={editPlan} onChange={e => setEditPlan(e.target.value)}>
                      <option value="Standard">Standard</option>
                      <option value="Plus">Plus</option>
                      <option value="Premium">Premium</option>
                      <option value="Metal">Metal</option>
                    </select>
                  </div>
                </div>
                <button disabled={loading}
                  onClick={() => run(() => adminApi('POST', 'update_profile', { type: 'update_profile', user_id: client.id, first_name: editFirstName, last_name: editLastName, phone: editPhone, kyc_status: editKyc, plan: editPlan }))}
                  className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <Edit3 className="w-4 h-4" />{loading ? 'Zapisywanie...' : 'Zapisz profil'}
                </button>
              </div>
            )}

            {tab === 'freeze' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 font-semibold text-sm mb-1">
                  <Shield className="w-5 h-5 text-gray-700" /> Zarządzanie statusem konta
                </div>
                {acc ? (
                  <>
                    <div className={`p-4 rounded-xl border-2 text-center ${acc.status === 'frozen' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                      <p className="text-2xl mb-1">{acc.status === 'frozen' ? '🔒' : '✅'}</p>
                      <p className={`font-bold text-lg ${acc.status === 'frozen' ? 'text-red-700' : 'text-green-700'}`}>
                        Konto jest {acc.status === 'frozen' ? 'ZAMROŻONE' : 'AKTYWNE'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">{client.first_name} {client.last_name} · {acc.account_number}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <button disabled={loading || acc.status === 'active'}
                        onClick={() => run(() => adminApi('POST', 'toggle_freeze', { type: 'toggle_freeze', account_id: acc.id, user_id: client.id, freeze: false }))}
                        className="py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                        <Unlock className="w-4 h-4" /> Odblokuj
                      </button>
                      <button disabled={loading || acc.status === 'frozen'}
                        onClick={() => run(() => adminApi('POST', 'toggle_freeze', { type: 'toggle_freeze', account_id: acc.id, user_id: client.id, freeze: true }))}
                        className="py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                        <Lock className="w-4 h-4" /> Zamroź
                      </button>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Wyślij ostrzeżenie</p>
                      <div className="flex gap-2">
                        <input placeholder="Treść ostrzeżenia..." className="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white"
                          id={`warn-${client.id}`} />
                        <button disabled={loading}
                          onClick={() => {
                            const el = document.getElementById(`warn-${client.id}`) as HTMLInputElement;
                            if (!el?.value) return;
                            run(() => adminApi('POST', 'notify', { type: 'notify', user_id: client.id, title: '⚠️ Ważna informacja', message: el.value, notif_type: 'alert' }));
                            el.value = '';
                          }}
                          className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-sm font-semibold disabled:opacity-40">
                          <AlertTriangle className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  <p className="text-gray-500 text-sm">Brak konta bankowego. Utwórz je najpierw w zakładce Konto.</p>
                )}
              </div>
            )}

            {tab === 'loan' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-amber-700 font-semibold text-sm mb-1">
                  <DollarSign className="w-5 h-5" /> Pożyczka / Kredyt
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Typ pożyczki</label>
                  <select className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={loanType} onChange={e => setLoanType(e.target.value)}>
                    <option value="personal">Osobista (5.9% APR)</option>
                    <option value="business">Biznesowa (4.5% APR)</option>
                    <option value="mortgage">Hipoteka (3.2% APR)</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Kwota pożyczki (GBP) *</label>
                  <input type="number" min="100" step="100" placeholder="np. 5000"
                    className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={loanAmt} onChange={e => setLoanAmt(e.target.value)} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Okres spłaty (miesiące)</label>
                  <select className="mt-1 w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white"
                    value={loanTerm} onChange={e => setLoanTerm(e.target.value)}>
                    <option value="6">6 miesięcy</option>
                    <option value="12">12 miesięcy</option>
                    <option value="24">24 miesiące</option>
                    <option value="36">36 miesięcy</option>
                    <option value="60">60 miesięcy</option>
                  </select>
                </div>
                {loanAmt && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Oprocentowanie:</span>
                      <span className="font-semibold text-amber-700">{interestRate}% APR</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Miesięczna rata:</span>
                      <span className="font-bold text-amber-800">{fmt(monthlyPayment)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Całkowita kwota:</span>
                      <span className="font-bold text-gray-900">{fmt(parseFloat(loanAmt) * (1 + interestRate / 100))}</span>
                    </div>
                  </div>
                )}
                <button disabled={loading || !loanAmt || !acc}
                  onClick={() => run(() => adminApi('POST', 'approve_loan', { type: 'approve_loan', account_id: acc!.id, user_id: client.id, loan_amount: parseFloat(loanAmt), loan_type: loanType, term_months: parseInt(loanTerm), interest_rate: interestRate }))}
                  className="w-full py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />{loading ? 'Zatwierdzanie...' : 'Zatwierdź pożyczkę'}
                </button>
              </div>
            )}

            {tab === 'verify' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-green-700 font-semibold text-sm mb-1">
                  <UserCheck className="w-5 h-5" /> Szybka weryfikacja KYC
                </div>
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <p className="text-sm text-gray-700 mb-2">Aktualny status KYC:</p>
                  <span className={`inline-block text-xs px-3 py-1 rounded-full border font-medium ${kycInfo.cls}`}>{kycInfo.label}</span>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <p className="text-xs text-blue-700 mb-2">ℹ️ Ta akcja:</p>
                  <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                    <li>Ustawi status KYC na "verified"</li>
                    <li>Wyśle powiadomienie o pomyślnej weryfikacji</li>
                    <li>Odblokuje pełny dostęp do usług</li>
                  </ul>
                </div>
                <button disabled={loading || client.kyc_status === 'verified'}
                  onClick={() => run(() => adminApi('POST', 'verify_kyc', { type: 'verify_kyc', user_id: client.id }))}
                  className="w-full py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                  <CheckCircle className="w-4 h-4" />{loading ? 'Weryfikowanie...' : client.kyc_status === 'verified' ? 'Już zweryfikowane' : 'Zweryfikuj KYC'}
                </button>
              </div>
            )}

            {tab === 'addaccount' && (
              <div className="max-w-md space-y-4">
                <div className="flex items-center gap-2 text-blue-700 font-semibold text-sm mb-1">
                  <PlusCircle className="w-5 h-5" /> Add / Manage Bank Account
                </div>
                {acc ? (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <p className="text-sm font-semibold text-green-700">Client has an active bank account</p>
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p><span className="font-medium">Account No:</span> {acc.account_number}</p>
                        <p><span className="font-medium">IBAN:</span> {acc.iban}</p>
                        <p><span className="font-medium">Balance:</span> {fmt(acc.balance ?? 0)}</p>
                        <p><span className="font-medium">Status:</span> {acc.status}</p>
                      </div>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                      <p className="text-xs text-yellow-700 font-medium mb-2">⚠️ To reset or create a new account (current will be replaced):</p>
                      <button disabled={loading}
                        onClick={() => {
                          if (window.confirm('This will create a NEW bank account. The existing account data will remain in the database but a new one will be created. Continue?')) {
                            run(() => adminApi('POST', 'create_account', { type: 'create_account', user_id: client.id, force: true }));
                          }
                        }}
                        className="w-full py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold text-xs disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                        <PlusCircle className="w-3.5 h-3.5" />{loading ? 'Creating...' : 'Force Create New Account'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                      <p className="text-xs text-blue-700 mb-2">ℹ️ This action will create:</p>
                      <ul className="text-xs text-blue-600 space-y-1 list-disc list-inside">
                        <li>New GBP bank account</li>
                        <li>Unique account number &amp; IBAN</li>
                        <li>Initial balance £0.00</li>
                        <li>Status: active</li>
                        <li>Default Visa debit card</li>
                      </ul>
                    </div>
                    <button disabled={loading}
                      onClick={() => run(() => adminApi('POST', 'create_account', { type: 'create_account', user_id: client.id }))}
                      className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold text-sm disabled:opacity-40 transition-colors flex items-center justify-center gap-2">
                      <PlusCircle className="w-4 h-4" />{loading ? 'Creating...' : 'Create Bank Account'}
                    </button>
                  </>
                )}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminPanel() {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [kycFilter, setKycFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const notify = useCallback((msg: string, ok: boolean) => {
    setToast({ msg, ok });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminApi('GET', 'clients');
      setClients(data.clients ?? []);
    } catch (e) {
      notify('Nie udało się załadować klientów: ' + String(e), false);
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = clients.filter(c => {
    if (kycFilter !== 'all' && c.kyc_status !== kycFilter) return false;
    if (planFilter !== 'all' && c.plan !== planFilter) return false;
    if (search && !`${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const stats = {
    total: clients.length,
    verified: clients.filter(c => c.kyc_status === 'verified').length,
    pending: clients.filter(c => ['pending', 'submitted'].includes(c.kyc_status ?? '')).length,
    balance: clients.reduce((s, c) => s + (c.bank_accounts?.[0]?.balance ?? 0), 0),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      <header className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-500 rounded-xl flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="font-bold text-gray-900 leading-tight text-base">CYF24 Bank</p>
              <p className="text-xs text-amber-600 font-semibold uppercase tracking-wider">Panel Administratora</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-xs bg-amber-100 text-amber-700 border border-amber-200 px-3 py-1 rounded-full font-mono font-semibold">
              SUPER ADMIN
            </span>
            <button onClick={load} className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded-lg transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Odśwież</span>
            </button>
            <a href="/#/" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Wyjdź</span>
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-6">

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Wszyscy klienci', val: stats.total, icon: <Users className="w-5 h-5" />, color: 'text-gray-800', bg: 'bg-white' },
            { label: 'Zweryfikowani', val: stats.verified, icon: <CheckCircle className="w-5 h-5" />, color: 'text-green-700', bg: 'bg-green-50' },
            { label: 'Oczekujące KYC', val: stats.pending, icon: <AlertTriangle className="w-5 h-5" />, color: 'text-orange-600', bg: 'bg-orange-50' },
            { label: 'Całkowite saldo', val: fmt(stats.balance), icon: <BarChart3 className="w-5 h-5" />, color: 'text-amber-700', bg: 'bg-amber-50' },
          ].map(s => (
            <div key={s.label} className={`${s.bg} border border-gray-200 rounded-2xl p-4 shadow-sm`}>
              <div className={`${s.color} mb-2`}>{s.icon}</div>
              <p className="text-xs text-gray-500 mb-0.5">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`}>{s.val}</p>
            </div>
          ))}
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm space-y-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              placeholder="Szukaj po nazwisku lub emailu..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:bg-white focus:border-amber-300 focus:outline-none"
              value={search} onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-gray-500 font-medium self-center">KYC:</span>
            {['all','verified','submitted','pending','rejected'].map(f => (
              <button key={f} onClick={() => setKycFilter(f)}
                className={`text-xs px-3 py-1 rounded-full border transition-all ${kycFilter === f ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                {f === 'all' ? 'Wszystkie' : f === 'verified' ? 'Zweryfikowane' : f === 'submitted' ? 'Przesłane' : f === 'pending' ? 'Oczekujące' : 'Odrzucone'}
              </button>
            ))}
            <span className="text-xs text-gray-500 font-medium self-center ml-2">Plan:</span>
            {['all','Standard','Plus','Premium','Metal'].map(f => (
              <button key={f} onClick={() => setPlanFilter(f)}
                className={`text-xs px-3 py-1 rounded-full border transition-all ${planFilter === f ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-600 border-gray-200 hover:border-amber-300'}`}>
                {f === 'all' ? 'Wszystkie' : f}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr] gap-4 px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <span>Klient</span><span>KYC</span><span>Plan</span><span>Saldo</span><span>Rejestracja</span>
          </div>

          {loading ? (
            <div className="space-y-3">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4 animate-pulse">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-gray-200 rounded w-40" />
                      <div className="h-3 bg-gray-100 rounded w-56" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center shadow-sm">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">Nie znaleziono klientów</p>
              <p className="text-gray-400 text-sm mt-1">Spróbuj dostosować filtry lub zarejestrować nowe konto</p>
              <button onClick={load} className="mt-4 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-xl text-sm font-semibold">
                Przeładuj
              </button>
            </div>
          ) : (
            filtered.map(client => (
              <ClientRow key={client.id} client={client} onRefresh={load} notify={notify} />
            ))
          )}
        </div>

        <div className="text-center text-xs text-gray-400 pb-6">
          © 2026 CYF SOLUTION LTD · Panel Administratora CYF24 Bank · {clients.length} klientów
        </div>
      </main>
    </div>
  );
}